import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { LineChart as LineChartIcon, PieChart, TrendingDown, TrendingUp } from "lucide-react";
import { api } from "../api";
import { REPORTS } from "../constants/testIds";
import { useAppData } from "../contexts/AppDataContext";
import { formatCurrency, hasPermission } from "../lib/format";
import { fadeUp } from "../lib/motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";

const TABS = [
  { key: "pnl", label: "P&L" }, { key: "expenses", label: "Expense breakdown" },
  { key: "retail", label: "Retail margin" },
  { key: "marketing", label: "Marketing ROI", permission: "marketing.view" },
  { key: "cohort", label: "Cohort & benchmarking" },
];

// Marketing ROI, read straight from the Marketing module's attribution so Reports and the
// Marketing tab can never disagree about what an ad campaign returned.
function MarketingRoiTab() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["reports-marketing-roi"], queryFn: async () => (await api.get("/marketing/attribution")).data });

  if (q.isLoading) return <GymLoader label="Loading marketing ROI…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} title="Couldn't load marketing ROI" />;

  const { totals, campaigns } = q.data;
  const rows = campaigns.filter((c) => c.attribution.spend > 0 || c.attribution.converted_members > 0);

  if (!rows.length) {
    return (
      <div className="solid-surface rounded-[14px] p-8 text-center space-y-3">
        <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
          No ad spend recorded yet. Once you run a campaign and log what it cost, its return shows here.
        </p>
        <Button onClick={() => navigate("/app/marketing")} className="text-white" style={{ background: "var(--scarlet-accent)" }}>
          Go to Marketing
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Ad spend" value={formatCurrency(totals.spend)} />
        <Metric label="Revenue from ads" value={formatCurrency(totals.revenue)} />
        <Metric label="Net" value={formatCurrency(totals.net)} />
        <Metric label="Return on ad spend" value={totals.roi_multiple != null ? `${totals.roi_multiple}×` : "—"} />
      </div>

      <div className="solid-surface rounded-[14px] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
              {["Campaign", "Spend", "Revenue", "Members", "Return", "Still paying"].map((h) => (
                <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold" style={{ color: "var(--scarlet-text-secondary)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <td className="px-4 py-2.5" style={{ color: "var(--scarlet-text)" }}>{c.name}</td>
                <td className="px-4 py-2.5 tabular-nums" style={{ color: "var(--scarlet-text-secondary)" }}>{formatCurrency(c.attribution.spend)}</td>
                <td className="px-4 py-2.5 tabular-nums font-semibold" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(c.attribution.revenue)}</td>
                <td className="px-4 py-2.5 tabular-nums" style={{ color: "var(--scarlet-text-secondary)" }}>{c.attribution.converted_members}</td>
                <td className="px-4 py-2.5 tabular-nums font-semibold"
                  style={{ color: (c.attribution.roi_multiple || 0) >= 1 ? "var(--scarlet-pos)" : "var(--scarlet-neg)" }}>
                  {c.attribution.roi_multiple != null ? `${c.attribution.roi_multiple}×` : "—"}
                </td>
                <td className="px-4 py-2.5 tabular-nums" style={{ color: "var(--scarlet-text-secondary)" }}>
                  {c.attribution.retention.still_active}
                  {c.attribution.retention.avg_tenure_months != null ? ` · ${c.attribution.retention.avg_tenure_months} mo avg` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
        Ad spend also appears under “marketing” in the Expense breakdown and in P&L — this view adds what that
        spend brought back. Full detail in <button className="underline" onClick={() => navigate("/app/marketing")}>Marketing</button>.
      </p>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="solid-surface rounded-[14px] p-4">
      <p className="eyebrow">{label}</p>
      <p className="text-xl font-bold mt-1 tabular-nums" style={{ color: "var(--scarlet-text)" }}>{value}</p>
    </div>
  );
}

// UX-GAP-01 §1.8: Owner Cockpit "Reports & Analytics" — was a StubPage.
export default function Reports() {
  const { branches, selectedBranchId, me } = useAppData();
  const tabs = TABS.filter((t) => !t.permission || hasPermission(me, t.permission));
  const reducedMotion = useReducedMotion();
  const [tab, setTab] = useState("pnl");
  const [branchId, setBranchId] = useState(selectedBranchId !== "all" ? selectedBranchId : "");

  return (
    <div className="space-y-5 max-w-4xl" data-testid={REPORTS.page}>
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Reports & Analytics</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Where the money's going, branch by branch.</p>
        </div>
        {tab !== "cohort" && tab !== "marketing" && (
          <Select onValueChange={(v) => setBranchId(v === "all" ? "" : v)} value={branchId || "all"}>
            <SelectTrigger data-testid={REPORTS.branchSelect} className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </motion.div>

      <div className="flex gap-1.5 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.key} data-testid={REPORTS.tab(t.key)} onClick={() => setTab(t.key)}
            className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
            style={tab === t.key ? { background: "var(--scarlet-accent)", color: "#fff" } : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "pnl" && <PnlTab branchId={branchId} />}
      {tab === "expenses" && <ExpenseTab branchId={branchId} />}
      {tab === "retail" && <RetailMarginTab branchId={branchId} />}
      {tab === "marketing" && <MarketingRoiTab />}
      {tab === "cohort" && <CohortTab />}
    </div>
  );
}

function PnlTab({ branchId }) {
  const q = useQuery({ queryKey: ["reports-pnl", branchId], queryFn: async () => (await api.get("/reports/pnl", { params: { branch_id: branchId || undefined } })).data });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading) return <GymLoader label="Loading P&L…" />;
  const isProfit = q.data.profit >= 0;
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <div data-testid={REPORTS.pnlRevenue} className="solid-surface rounded-[14px] px-4 py-4"><p className="eyebrow">Revenue</p><p className="text-xl font-bold mt-1 tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(q.data.revenue)}</p></div>
        <div data-testid={REPORTS.pnlExpense} className="solid-surface rounded-[14px] px-4 py-4"><p className="eyebrow">Expenses</p><p className="text-xl font-bold mt-1 tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(q.data.expense)}</p></div>
        <div data-testid={REPORTS.pnlProfit} className="solid-surface rounded-[14px] px-4 py-4">
          <p className="eyebrow flex items-center gap-1">{isProfit ? <TrendingUp size={12} /> : <TrendingDown size={12} />} Profit</p>
          <p className="text-xl font-bold mt-1 tabular-nums-force" style={{ color: isProfit ? "#0f8a3f" : "var(--scarlet-neg)" }}>{formatCurrency(q.data.profit)}</p>
        </div>
      </div>
      <div className="solid-surface rounded-[14px] p-5">
        <p className="eyebrow mb-3 flex items-center gap-1.5"><LineChartIcon size={13} /> Daily trend</p>
        <div className="space-y-2">
          {q.data.daily_trend.map((d) => (
            <div key={d.date} data-testid={REPORTS.pnlTrendRow(d.date)} className="flex items-center justify-between text-sm">
              <span style={{ color: "var(--scarlet-text-secondary)" }}>{d.date}</span>
              <span className="tabular-nums-force" style={{ color: d.profit >= 0 ? "#0f8a3f" : "var(--scarlet-neg)" }}>{formatCurrency(d.profit)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExpenseTab({ branchId }) {
  const q = useQuery({ queryKey: ["reports-expense-breakdown", branchId], queryFn: async () => (await api.get("/reports/expense-breakdown", { params: { branch_id: branchId || undefined } })).data });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading) return <GymLoader label="Loading expenses…" />;
  const entries = Object.entries(q.data.by_category).sort((a, b) => b[1] - a[1]);
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="solid-surface rounded-[14px] p-5 space-y-3" data-testid={REPORTS.expenseByCategory}>
      <p className="eyebrow flex items-center gap-1.5"><PieChart size={13} /> By category</p>
      {entries.length === 0 && <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No expenses recorded yet.</p>}
      {entries.map(([cat, amt]) => (
        <div key={cat} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="capitalize" style={{ color: "var(--scarlet-text)" }}>{cat}</span>
            <span className="font-medium tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(amt)}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--scarlet-hairline)" }}>
            <div className="h-full rounded-full" style={{ width: `${(amt / max) * 100}%`, background: "var(--scarlet-accent)" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function RetailMarginTab({ branchId }) {
  const q = useQuery({ queryKey: ["reports-retail-margin", branchId], queryFn: async () => (await api.get("/reports/retail-margin", { params: { branch_id: branchId || undefined } })).data });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading) return <GymLoader label="Loading retail margin…" />;
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3" data-testid={REPORTS.retailMarginTotal}>
        <div className="solid-surface rounded-[14px] px-4 py-4"><p className="eyebrow">Retail revenue</p><p className="text-xl font-bold mt-1 tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(q.data.total_revenue)}</p></div>
        <div className="solid-surface rounded-[14px] px-4 py-4"><p className="eyebrow">COGS</p><p className="text-xl font-bold mt-1 tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(q.data.total_cogs)}</p></div>
        <div className="solid-surface rounded-[14px] px-4 py-4"><p className="eyebrow">Margin</p><p className="text-xl font-bold mt-1 tabular-nums-force" style={{ color: "#0f8a3f" }}>{formatCurrency(q.data.total_margin)}</p></div>
      </div>
      <p data-testid={REPORTS.retailMarginNote} className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{q.data.margin_note}</p>
    </div>
  );
}

// Cohort/retention analytics and cross-gym benchmarking have no backend endpoint at all in this
// schema — flagged honestly rather than a fabricated chart, per the doc's own "flag, don't
// fabricate" ground rule.
function CohortTab() {
  return (
    <div className="solid-surface rounded-[14px] p-8 flex flex-col items-center text-center gap-3" data-testid={REPORTS.cohortNotAvailable}>
      <LineChartIcon size={22} style={{ color: "var(--scarlet-text-secondary)" }} />
      <p className="font-semibold" style={{ color: "var(--scarlet-text)" }}>Cohort retention & gym benchmarking aren't built yet</p>
      <p className="text-sm max-w-md" style={{ color: "var(--scarlet-text-secondary)" }}>
        This needs a dedicated analytics pipeline we haven't built — flagging it here honestly instead of showing you invented numbers.
      </p>
    </div>
  );
}
